// app/javascript/controllers/table_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["table"];
  static values = { features: String }

  connect() {
    // Parse features from data attribute (e.g., "search,sort,export")
    this.enabledFeatures = (this.featuresValue || "search,sort,export")
      .split(",")
      .map(f => f.trim());

    if (this.enabledFeatures.includes("search")) {
      try {
        this.createSearchInput();
      } catch (e) {
        console.error("Error in createSearchInput:", e);
      }
    }
    if (this.enabledFeatures.includes("sort")) {
      try {
        this.addSortIconsToHeaders();
      } catch (e) {
        console.error("Error in addSortIconsToHeaders:", e);
      }
    }
    if (this.enabledFeatures.includes("export")) {
      try {
        this.createExportButton();
      } catch (e) {
        console.error("Error in createExportButton:", e);
      }
    }

  }

  createExportButton() {
    // Create the export button element
    const button = document.createElement('button');
    button.id = 'export-csv-button';
    button.title = 'Export the current table to a CSV file';
    button.setAttribute('data-action', 'click->table#exportToCSV');
    button.setAttribute('class', 'bg-blue-500 text-white px-3 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 float-right ml-auto flex items-center hover:cursor-pointer');

    // Add export icon SVG (download icon)
    button.innerHTML = `
      <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"/>
      </svg>
      <span class="sr-only md:not-sr-only">Export</span>
    `;

    // Insert the button at the beginning of the controller's element
    const existingButton = this.element.querySelector('button[data-action="table#exportToCSV"]');
    if (!existingButton) {
      this.element.insertBefore(button, this.element.firstChild);
    }
  }

  exportToCSV() {
    const table = this.element.querySelector('table');
    const rows = table.querySelectorAll('tr');
    const csvContent = Array.from(rows).map(row => {
      // Only include visible rows (skip filtered out rows)
      if (row.style.display === 'none') return null;
      const cells = row.querySelectorAll('th, td');
      return Array.from(cells).map(cell => {
      // Get only the visible text, trim whitespace, and collapse multiple spaces/newlines
      let cellText = cell.textContent
        .replace(/\s+/g, ' ') // collapse whitespace
        .trim();
      // Escape double quotes and wrap in quotes
      cellText = cellText.replace(/"/g, '""');
      return `"${cellText}"`;
      }).join(',');
    }).filter(Boolean).join('\n');
    // Create a Blob from the CSV content
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    // Create a link to download the Blob
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);

    // Get timestamp and tab title (only before "Mission Control")
    const now = new Date();
    const timestamp = now.toISOString().replace(/[-:T]/g, '').slice(0, 14); // Only YYYYMMDDHHMMSS

    // Extract part of the title before "Mission Control"
    let title = document.title.split('Mission Control')[0]
      .replace(/[^a-z0-9]+/gi, '-')      // Replace non-alphanumerics with hyphens
      .replace(/^-+|-+$/g, '')           // Trim leading/trailing hyphens
      .replace(/-+/g, '-')               // Collapse multiple hyphens
      .toLowerCase();

    // Fallback to 'table' if title is empty to avoid double dots in filename
    if (!title) title = 'table';

    link.download = `${timestamp}-${title}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  createSearchInput() {
    // Create the input element
    const input = document.createElement('input');
    input.type = 'search';
    input.placeholder = 'Search records on screen...';
    input.title = 'If records are paginated (ie not all records are visible) then this field will only search/sort the records you see...';
    input.setAttribute('data-action', 'input->table#filterRows');
    input.setAttribute('class', 'bg-white rounded-md border border-sapphire placeholder-gray-300 w-full md:w-1/3 sticky z-30 top-12');

    // Insert the input at the beginning of the controller's element
    // Check if the input element already exists
    const existingInput = this.element.querySelector('input[type="search"]');
    if (!existingInput) {
      // Insert the input at the beginning of the controller's element
      this.element.insertBefore(input, this.element.firstChild);
    }
  }

  filterRows(event) {
    // Get the input value
    const sText = event.target.value.toLowerCase();
    // determine if the input value is exclusionary
    const isExclusionary = sText.startsWith('-');
    const searchText = isExclusionary ? sText.slice(1) : sText;
    // Get the table within the controller's element
    const table = this.element.querySelector('table');
    // Iterate over all rows in the table body
    const rows = table.querySelectorAll('tbody tr');

    // Clear any existing highlights
    this.clearHighlights();

    // Iterate over all rows in the table body
    if (searchText.length === 0) {
      // If the search text is empty, show all rows
      rows.forEach(row => row.style.display = '');
      return;
    }

    // Parse search terms (split by spaces unless quoted)
    const searchTerms = this.parseSearchTerms(searchText);

    rows.forEach(row => {
      // Check if any cell in this row contains all search terms
      const matches = this.checkRowMatches(row, searchTerms);

      // Show or hide the row based on whether there was a match
      if (isExclusionary) {
        row.style.display = (matches ? 'none' : '');
        // Don't highlight for exclusionary searches
      } else {
        row.style.display = (matches ? '' : 'none');
        // Highlight matching terms if row is visible
        if (matches) {
          this.highlightMatches(row, searchTerms);
        }
      }
    });
  }

  parseSearchTerms(searchText) {
    const terms = [];
    let currentTerm = '';
    let inQuotes = false;
    let i = 0;

    while (i < searchText.length) {
      const char = searchText[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ' ' && !inQuotes) {
        if (currentTerm.trim()) {
          terms.push(currentTerm.trim());
          currentTerm = '';
        }
      } else {
        currentTerm += char;
      }
      i++;
    }

    if (currentTerm.trim()) {
      terms.push(currentTerm.trim());
    }

    return terms.filter(term => term.length > 0);
  }

  checkRowMatches(row, searchTerms) {
    const rowText = Array.from(row.cells).map(cell => cell.textContent.toLowerCase()).join(' ');

    // All terms must be found for a match (AND logic)
    return searchTerms.every(term => rowText.includes(term));
  }

  highlightMatches(row, searchTerms) {
    Array.from(row.cells).forEach(cell => {
      this.highlightTermsInElement(cell, searchTerms);
    });
  }

  highlightTermsInElement(element, searchTerms) {
    // Skip elements that contain interactive elements like buttons or forms
    if (element.querySelector('button, input, select, textarea, svg, span[data-action], a[data-action]')) {
      return;
    }

    // Skip if this element itself has data-action attributes
    if (element.hasAttribute('data-action')) {
      return;
    }

    // Skip if this element already contains highlights to avoid double-processing
    if (element.querySelector('mark.bg-yellow-200')) {
      return;
    }

    // Store original text content before any highlighting
    const originalText = element.textContent;

    // Check if any search terms match the original text
    let hasMatches = false;
    let processedText = originalText;

    searchTerms.forEach(term => {
      const regex = new RegExp(`(${this.escapeRegex(term)})`, 'gi');
      if (regex.test(processedText)) {
        hasMatches = true;
        // Replace with a simple placeholder first to avoid nested replacements
        processedText = processedText.replace(regex, `||HIGHLIGHT_START||$1||HIGHLIGHT_END||`);
      }
    });

    if (hasMatches) {
      // Only process elements that contain just text or simple inline elements
      // Avoid complex nested structures
      if (this.isSimpleTextElement(element)) {
        // Clear the element and rebuild with proper highlighting
        element.innerHTML = '';

        // Split by our placeholders and rebuild
        const parts = processedText.split(/\|\|HIGHLIGHT_(START|END)\|\|/);
        let isHighlight = false;

        parts.forEach(part => {
          if (part === 'START') {
            isHighlight = true;
          } else if (part === 'END') {
            isHighlight = false;
          } else if (part.length > 0) {
            if (isHighlight) {
              const mark = document.createElement('mark');
              mark.className = 'bg-yellow-200 text-gray-900';
              mark.textContent = part;
              element.appendChild(mark);
            } else {
              element.appendChild(document.createTextNode(part));
            }
          }
        });
      }
    }
  }

  isSimpleTextElement(element) {
    // Check if element contains only text nodes and simple inline elements
    for (let child of element.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        continue; // Text nodes are fine
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const tagName = child.tagName.toUpperCase();
        // Only allow simple inline elements
        if (!['SPAN', 'EM', 'STRONG', 'B', 'I', 'SMALL'].includes(tagName)) {
          return false;
        }
        // Check if this child has data-action or other interactive attributes
        if (child.hasAttribute('data-action') || child.querySelector('[data-action]')) {
          return false;
        }
      } else {
        return false; // Other node types not allowed
      }
    }
    return true;
  }  clearHighlights() {
    // Store original text content for each cell before clearing
    const table = this.element.querySelector('table');
    if (!table) return;

    // Find all cells with highlights and restore their original text
    const cellsWithHighlights = table.querySelectorAll('td:has(mark.bg-yellow-200), th:has(mark.bg-yellow-200)');

    cellsWithHighlights.forEach(cell => {
      // Get the plain text content (this will strip all HTML including marks)
      const plainText = cell.textContent;

      // Check if this is a simple text cell that we can safely replace
      if (this.isSimpleTextElement(cell) || cell.querySelector('mark.bg-yellow-200')) {
        // Clear the cell and add back just the plain text
        cell.innerHTML = '';
        cell.appendChild(document.createTextNode(plainText));
      }
    });

    // Also handle any marks that might exist elsewhere
    const allMarks = this.element.querySelectorAll('mark.bg-yellow-200');
    allMarks.forEach(mark => {
      const parent = mark.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(mark.textContent), mark);
        parent.normalize();
      }
    });

    // Clean up any leftover broken HTML by finding text content with stray markup
    const allCells = table.querySelectorAll('td, th');
    allCells.forEach(cell => {
      const innerHTML = cell.innerHTML;
      // Check for broken mark tags or malformed HTML
      if (innerHTML.includes('<mark class="<mark') ||
          innerHTML.includes('bg-yellow-200=""') ||
          innerHTML.includes('text-gray-900"="">')) {
        // This cell has broken markup, restore to plain text
        cell.innerHTML = '';
        cell.appendChild(document.createTextNode(cell.textContent));
      }
    });
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  addSortIconsToHeaders() {
    const table = this.element.querySelector('table');
    const headers = table.querySelectorAll('th');

    headers.forEach((header, index) => {
        // Wrap the existing header text and sorting icons in a container for better control
        const headerText = header.innerText.trim();
        if (headerText.length === 0) return; // Skip empty headers

        const sortingSpan = document.createElement('div');
        sortingSpan.classList.add('flex', 'justify-between', 'items-center', 'w-full', 'hover:cursor-pointer', 'group'); // Tailwind classes for flexbox layout
        sortingSpan.innerHTML = `
            <span class="flex-grow">${headerText}</span>
            <span class="flex items-center" data-column-index="${index}">
                <svg class="sort-desc h-5 w-5 font-semibold" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style="display: block;"><path d="M5.293 14.707a1 1 0 001.414 0L10 11.414l3.293 3.293a1 1 0 101.414-1.414l-4-4a1 1 0 00-1.414 0l-4 4a1 1 0 000 1.414z"/></svg>
                <svg class="sort-asc  h-5 w-5 font-semibold" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style="display: none;"><path d="M14.707 5.293a1 1 0 00-1.414 0L10 8.586 6.707 5.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l4-4a1 1 0 000-1.414z"/></svg>
            </span>`;
        // Clear the current header and append the new content
        header.innerHTML = '';
        header.setAttribute('data-action', 'click->table#sortColumn');
        header.setAttribute('data-column-index', `${index}`);
        header.appendChild(sortingSpan);
    });
  }

  sortColumn(event) {
    // Find out if the click was on an icon or elsewhere within the span.
    const clickedElement = event.target;
    const isAscendingIcon = clickedElement.classList.contains('sort-asc');
    const isDescendingIcon = clickedElement.classList.contains('sort-desc');

    // Get the index of the column clicked for sorting
    const columnIndex = parseInt(event.currentTarget.getAttribute('data-column-index'));

    // Determine new sorting direction
    let newSortDirection;
    if (isAscendingIcon) {
        newSortDirection = 'ascending';
    } else if (isDescendingIcon) {
        newSortDirection = 'descending';
    } else {
        // If clicked outside the icons, toggle based on previous state
        newSortDirection = this.sortDirectionValue === 'ascending' ? 'descending' : 'ascending';
    }

    // Find the table and its rows
    const table = this.element.querySelector('table');
    const rowsArray = Array.from(table.querySelectorAll('tbody tr'));

    // Helper function to get sort value from a cell
    const getSortValue = (row, columnIndex) => {
        const cell = row.querySelector(`td:nth-child(${columnIndex + 1})`);
        if (!cell) return '';

        // First check for data-sort-value attribute
        const dataSortValue = cell.getAttribute('data-sort-value');
        if (dataSortValue !== null) {
            return dataSortValue;
        }

        // Fall back to innerText
        return cell.innerText.trim();
    };

    // Determine if the column contains numeric values
    const isNumericColumn = rowsArray.every(row => {
        const cellValue = getSortValue(row, columnIndex);
        return !isNaN(cellValue) && !isNaN(parseFloat(cellValue)) && cellValue !== '';
    });

    // Perform the sort
    rowsArray.sort((a, b) => {
        const aValue = getSortValue(a, columnIndex);
        const bValue = getSortValue(b, columnIndex);

        // Convert to numbers if the column is numeric
        const numA = isNumericColumn ? parseFloat(aValue) : aValue;
        const numB = isNumericColumn ? parseFloat(bValue) : bValue;

        // Handle empty values (put them at the end)
        if (aValue === '' && bValue === '') return 0;
        if (aValue === '') return 1;
        if (bValue === '') return -1;

        // Sorting for ascending or descending
        if (newSortDirection === 'ascending') {
            return isNumericColumn ? numA - numB : numA.localeCompare(numB);
        } else {
            return isNumericColumn ? numB - numA : bValue.localeCompare(aValue);
        }
    });

    // Re-append rows to tbody in new order
    const tbody = table.querySelector('tbody');
    rowsArray.forEach(row => tbody.appendChild(row));

    // Set the new sort direction
    this.sortDirectionValue = newSortDirection;
    this.sortColumnValue = columnIndex;

    const headers = this.element.querySelectorAll('th');
    headers.forEach((header, index) => {
        // Skip empty headers
        if (header.innerText.trim().length === 0) return;

        const ascIcon = header.querySelector('.sort-asc');
        const descIcon = header.querySelector('.sort-desc');

        if (index === columnIndex) {
            // This is the clicked column, update icons to reflect the new sort direction
            if (newSortDirection === 'ascending') {
              ascIcon.classList.remove('text-canary');
              descIcon.classList.remove('invisible','group-hover:visible');
              descIcon.classList.add('text-canary');
              ascIcon.style.display = 'none'; // hide
              descIcon.style.display = 'inline'; // show as default
            } else {
                descIcon.classList.remove('text-canary');
                ascIcon.classList.remove('invisible','group-hover:visible');
                ascIcon.classList.add('text-canary');
                ascIcon.style.display = 'inline'; // show as default
                descIcon.style.display = 'none'; // hide
            }
        } else {
            // For other columns, you might want to reset icons to indicate they're not sorted
            // Remove the 'text-canary' class to signify that they are not the active sort
            ascIcon.classList.remove('text-canary');
            descIcon.classList.remove('text-canary');
            ascIcon.classList.add('invisible','group-hover:visible');
            descIcon.classList.add('invisible','group-hover:visible');
            // Or you can choose to leave them as is, depending on how you want your UI to behave
            // If you want to reset, uncomment the following lines:
            ascIcon.style.display = 'inline'; // Show as default, indicating sortable but not currently sorted
            descIcon.style.display = 'none'; // Hide by default
        }
    });
  }
}